import { PROPERTIES_SELECTION } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.constants';
import useAppModal from 'hooks/useAppModal';
import {
  AirbnbFeeType,
  AirbnbFeeUnitType,
  BookingDotComFeeType,
  CleaningFeeCollection,
  FeeAmountTypeEnum,
  FeeConditionEnum,
  FeeTypeEnum,
  HostfullyFeeType,
  HVMIFeeType,
  PropertyInternalFee,
  VrboFeeType,
} from 'models/PropertyFees';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import { isNotSelectAll } from 'components/forms/multiSelectField/MultiSelectField.constants';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import { Channel } from 'models/Channels';
import PropertyFeeModal, {
  ADD_PROPERTY_FEE_MODAL_ID,
  EDIT_PROPERTY_FEE_MODAL_ID,
} from './PropertyFeeModal';
import { PropertyFeeFormSchemaType } from './PropertyFeeModal.schema';
import usePropertyFeeModalQueries, {
  ADD_PROPERTIES_FEES_BASE_QUERY_KEY,
  UPDATE_PROPERTIES_FEES_BASE_QUERY_KEY,
} from './usePropertyFeeModalQueries';
import {
  AIRBNB_OFFLINE_TYPE_OPTIONS,
  BOOKING_PERCENT_APPLIES_TO_OPTIONS,
  BY_LENGTH_OF_STAY_SCOPE_OPTION,
} from './PropertyFeeModal.constants';
import { getBookingDotComChargeBasesFromPercentAppliesTo } from './PropertyFeeModal.utils';

const useOpenPropertyFeeModal = () => {
  const { openModal } = useAppModal();
  const { createFee, updateFee } = usePropertyFeeModalQueries();
  const { property, isLoading } = usePropertySettingsInitData();
  const { channelSettings } = useFetchAgencyChannelSettings();

  const propertyCurrency =
    property?.pricing?.currency && property.pricing.currency !== 'NONE'
      ? property.pricing.currency
      : 'USD';

  const handleSubmit = (
    values: PropertyFeeFormSchemaType,
    type: 'EDIT' | 'CREATE',
  ) => {
    const { amountType, cleaningFeeCollectionMappingType } = values;
    const conditionsFormValues = values.tax?.conditions as {
      lengthOfStayValue: number;
      lengthOfStayType: FeeConditionEnum;
      type: FeeConditionEnum | typeof BY_LENGTH_OF_STAY_SCOPE_OPTION;
      firstNightsApplying: number;
    };

    const airbnbType =
      (values.airbnbMappingType as AirbnbFeeType | undefined) || null;

    // We must sent offline as true both when the user selects it from the Cleaning Fee Mapping type AND when using an
    // Airbnb Offline fee.
    const offline =
      (airbnbType && AIRBNB_OFFLINE_TYPE_OPTIONS.includes(airbnbType)) ||
      (airbnbType === AirbnbFeeType.PASS_THROUGH_CLEANING_FEE &&
        cleaningFeeCollectionMappingType === CleaningFeeCollection.OFFLINE);

    const getConditionPayload = () => {
      const isByLengthOfStayCondition =
        conditionsFormValues?.type === BY_LENGTH_OF_STAY_SCOPE_OPTION;

      const isExemptLongStayCondition =
        isByLengthOfStayCondition &&
        conditionsFormValues?.lengthOfStayType ===
          FeeConditionEnum.EXEMPT_LONG_STAY;

      const isExclusiveLongStayCondition =
        isByLengthOfStayCondition &&
        conditionsFormValues?.lengthOfStayType ===
          FeeConditionEnum.EXCLUSIVE_LONG_STAY;

      const isByNightCondition =
        conditionsFormValues?.type === FeeConditionEnum.BY_NIGHT;

      const condition = isByLengthOfStayCondition
        ? conditionsFormValues?.lengthOfStayType
        : conditionsFormValues?.type;

      return {
        condition,
        conditionsLOSDays: isExclusiveLongStayCondition
          ? conditionsFormValues?.lengthOfStayValue
          : 0,
        longTermStayExemption: isExemptLongStayCondition
          ? conditionsFormValues?.lengthOfStayValue
          : 0,
        firstNightsApplying: isByNightCondition
          ? conditionsFormValues?.firstNightsApplying
          : 0,
      };
    };

    const hostfullyType =
      values.appliesToHostfully === 'YES' ? HostfullyFeeType.DEFAULT : null;

    const rollsIntoRent = values.rollsIntoRent === 'YES';

    const { scope } = values;

    const applyToAllProperties =
      values.appliesTo === PROPERTIES_SELECTION.ALL_PROPERTIES;

    const appliesToPropertiesFiltered =
      values.appliesToProperties?.filter(isNotSelectAll);

    const getBookingDotComChargeBases = () => {
      const isPercentageType = amountType === FeeAmountTypeEnum.TAX;
      const isBookingDotComEnabled = channelSettings?.some(
        ({ enumId, isActive }) => enumId === Channel.booking_com && isActive,
      );

      const shouldSendBookingChargeBases =
        isPercentageType && isBookingDotComEnabled;

      if (!shouldSendBookingChargeBases) {
        return null;
      }

      if (!values.bookingPercentAppliesTo) {
        return null;
      }

      return getBookingDotComChargeBasesFromPercentAppliesTo(
        values.bookingPercentAppliesTo as BOOKING_PERCENT_APPLIES_TO_OPTIONS,
      );
    };

    const payload = {
      uid: values?.uid,
      type: values.type as FeeTypeEnum,
      name: values.name,
      amount: values.amount,
      amountType,
      taxationRate: values.fee?.taxationRate,
      scope,
      conditions: getConditionPayload(),
      offline,
      optional: values.optional === 'YES',
      airbnbType,
      airbnbUnitType:
        (values.airbnbUnitMappingType as AirbnbFeeUnitType) || null,
      vrboType: (values.vrboMappingType as VrboFeeType) || null,
      bookingDotComType:
        (values.bookingMappingType as BookingDotComFeeType) || null,
      bookingDotComChargeBases: getBookingDotComChargeBases(),
      hvmiType: (values.hvmbMappingType as HVMIFeeType) || null,
      hostfullyType,
      rollsIntoRent,
      allProperties: applyToAllProperties,
      propertiesUids: applyToAllProperties
        ? []
        : // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          appliesToPropertiesFiltered.map((item) => item.value),
    };

    if (type === 'CREATE') {
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      createFee(payload);
    } else {
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      updateFee(payload);
    }
  };

  const openAddModal = () => {
    openModal({
      id: ADD_PROPERTY_FEE_MODAL_ID,
      customContent: (
        <PropertyFeeModal
          handleSubmit={(values) => handleSubmit(values, 'CREATE')}
          queryKey={[ADD_PROPERTIES_FEES_BASE_QUERY_KEY]}
          propertyCurrency={propertyCurrency}
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          property={property}
          isLoadingProperty={isLoading}
        />
      ),
    });
  };

  const openEditModal = (fee: PropertyInternalFee) => {
    openModal({
      id: EDIT_PROPERTY_FEE_MODAL_ID,
      customContent: (
        <PropertyFeeModal
          fee={fee}
          handleSubmit={(values) => handleSubmit(values, 'EDIT')}
          queryKey={[UPDATE_PROPERTIES_FEES_BASE_QUERY_KEY]}
          propertyCurrency={propertyCurrency}
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          property={property}
          isLoadingProperty={isLoading}
        />
      ),
    });
  };

  return {
    openAddModal,
    openEditModal,
  };
};

export default useOpenPropertyFeeModal;
